// src/app/shared/utils/report-state.types.ts

import { FilterRule } from './field-filters.types';

/** Increment this constant whenever the stored schema changes in a breaking way. */
export const REPORT_STATE_SCHEMA_VERSION = 1;

/**
 * Persisted snapshot of all user-configurable report settings.
 *
 * IMPORTANT: Only UI configuration is stored here.
 * Never store raw API responses, table row data, or computed totals.
 */
export interface ReportStateV1 {
  /** Schema version guard — must equal REPORT_STATE_SCHEMA_VERSION to be loaded. */
  _v: 1;
  /** Unix milliseconds timestamp of the last write. */
  _ts: number;

  // ── Column state ──────────────────────────────────────────────────────────
  /**
   * Ordered array of visible column keys matching the column picker's `key`
   * values.  Order is preserved so future column-ordering support is easy.
   */
  visibleColumns?: string[];
  /** Column display order (array of column keys in user-defined order). */
  columnOrder?: string[];
  /** Per-column pixel widths keyed by column key. */
  columnWidths?: Record<string, number>;

  // ── Date filter ───────────────────────────────────────────────────────────
  /**
   * Mode token selected by the user in the DateFilterComponent
   * (e.g. 'thisMonth', 'custom', 'academicYear', 'previousMonth', …).
   */
  dateFilterMode?: string;
  /** Effective from-date (YYYY-MM-DD). Meaningful for 'custom' / 'specificMonth'. */
  dateFromDate?: string;
  /** Effective to-date   (YYYY-MM-DD). Meaningful for 'custom' / 'specificMonth'. */
  dateToDate?: string;

  // ── Academic year ─────────────────────────────────────────────────────────
  /** Academic year ID selected in the layout filter strip. */
  academicYearId?: number;

  // ── Pagination ────────────────────────────────────────────────────────────
  /** AG-Grid pagination page size chosen by the user. */
  pageSize?: number;

  // ── Advanced field-filter panel ───────────────────────────────────────────
  /** Whether the filter panel was open when the user last left the report. */
  advancedFilterOpen?: boolean;
  /** Active FilterRule entries from the field-filter panel. */
  filterRules?: FilterRule[];

  // ── Sort ──────────────────────────────────────────────────────────────────
  /** AG-Grid column sort model. */
  sortModel?: Array<{ colId: string; sort: 'asc' | 'desc' }>;

  // ── Search ────────────────────────────────────────────────────────────────
  /** Full-text search term (for reports that expose their own search box). */
  searchText?: string;

  // ── Per-report custom filters ─────────────────────────────────────────────
  /**
   * Report-specific filter values that live outside the shared layout
   * (e.g. granularity, classId, divisionId, mandatoryFilter, status, …).
   *
   * Each report component is responsible for serialising these into this map
   * and deserialising them again on restore.  Only plain JSON-serialisable
   * values are safe here: string | number | boolean | null | arrays thereof.
   */
  customFilters?: Record<string, unknown>;
}

/**
 * A named configuration snapshot attached to a FavouriteReportEntry.
 *
 * Allows the same report to be bookmarked with multiple independent
 * configurations — e.g. "Pending Students – Class 5" vs "Current Month".
 */
export interface FavouriteConfig {
  /** UUID-style unique identifier. */
  id: string;
  /** User-defined label (e.g. "Pending Students – Class 5"). */
  name: string;
  /** Unix milliseconds timestamp of when this config was saved. */
  createdAt: number;
  /** Unix milliseconds timestamp of the last update. */
  updatedAt: number;
  /** The full UI state snapshot captured at the time of saving. */
  state: ReportStateV1;
}
